import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { getMerchantMe, fetchNewOrders, extendPrepTime } from "../api/axios/requests";
import { API_URL } from "../api/axios/instance";
import { io } from "socket.io-client";
import { useNavigate } from "react-router-dom";

const MerchantContext = createContext();

// Нэвтрэх хуудасны зам — App.js-д "/" дээр LoginScreen. ("/login" гэсэн route
// байхгүй тул өмнө нь хоосон цагаан дэлгэц гардаг байв.)
const LOGIN_PATH = "/";

/* Захиалгын жагсаалтын шинэчлэлт — 2 мин → 20 сек (2026-09-17). Жолооч «ирж
   байна / ирсэн» ба хугацааны сунгалт мерчантад хурдан харагдах ёстой. Хүсэлт
   хөнгөн (зөвхөн энэ рестораны 2–4 төлөвтэй захиалга). */
const POLL_MS = 20 * 1000;
// Шинэ захиалгын дуудлагыг ИЖИЛ (хүлээж аваагүй) захиалгад хэр давтамжтай сануулах.
const NEW_ORDER_REMIND_MS = 2 * 60 * 1000;

// Шинэ захиалга татах poll ДАРААЛАН хэдэн удаа бүтэлгүйтвэл "захиалга ирэхгүй
// байна" банер (components/sidebar) гаргах вэ. ~2 минут (урьдынхтай ижил хугацаа).
const POLL_FAIL_BANNER_THRESHOLD = Math.ceil((2 * 60 * 1000) / POLL_MS);

export function useMerchant() {
  return useContext(MerchantContext);
}

export function MerchantProvider({ children }) {
  const navigate = useNavigate();

  const [merchantInfo, setMerchantInfo] = useState({
    id: null,
    name: "",
    duration: "0",
    isOpen: false,
    role: "",
  });
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [newOrderCount, setNewOrderCount] = useState(0);
  // Шинэ захиалга ИЛРҮҮЛЭХ бүрд нэмэгдэнэ — new-order.js дуугаа ДАХИН эхлүүлэхэд
  // ашиглана (modal аль хэдийн нээлттэй байхад 2 дахь захиалга ирвэл дуу гарахгүй
  // байсныг засна).
  const [newOrderTick, setNewOrderTick] = useState(0);
  const [newOrderModalOpen, setNewOrderModalOpen] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [ordersPollFailing, setOrdersPollFailing] = useState(false);
  const pollFailCountRef = useRef(0);
  // Шинэ захиалгын дуудлага: өмнө мэдэгдсэн дугаарууд ба сүүлд дуугарсан цаг.
  const alertedIdsRef = useRef(new Set());
  const lastAlertAtRef = useRef(0);

  const toggleFullScreen = () => {
    setIsFullScreen((prev) => !prev);
  };

  const saveMerchantInfo = (updates) => {
    setMerchantInfo((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  const resetPollFailures = () => {
    pollFailCountRef.current = 0;
    setOrdersPollFailing(false);
  };

  const clearMerchantInfo = () => {
    localStorage.removeItem("token");
    resetPollFailures();
    // Шинэ захиалгын дуудлагын түүхийг цэвэрлэ — эс бөгөөс дараа нэвтэрсэн өөр
    // хэрэглэгчид хуучин захиалга дуугарахгүй/дуугарах зэрэг гажуудал үүснэ.
    alertedIdsRef.current.clear();
    lastAlertAtRef.current = 0;
    setMerchantInfo({
      id: null,
      name: "",
      duration: "0",
      isOpen: false,
      role: "",
      open_time: null,
      close_time: null,
      is_manually_closed: false,
    });
  };

  const fetchMerchantInfo = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      clearMerchantInfo();
      setInitialLoading(false);
      return;
    }

    try {
      const res = await getMerchantMe();
      if (res?.success && res?.data) {
        saveMerchantInfo(res.data);
      } else {
        console.error(res?.error || "Unable to fetch merchant info.");
        clearMerchantInfo();
        navigate(LOGIN_PATH);
      }
    } catch (error) {
      console.error("getMerchantMe error:", error);
      clearMerchantInfo();
      navigate(LOGIN_PATH);
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchMerchantInfo();
  }, []);

  /* `silent` — арын 20 сек тутмын шинэчлэлт: хүснэгтийн ачаалах дүрс гаргахгүй
     (эс бөгөөс жагсаалт 20 сек тутам анивчина). Гараар дарсан шалгалт дүрстэй. */
  const pollOrders = async (opts) => {
    const silent = opts?.silent === true;
    if (!silent) setOrdersLoading(true);
    let succeeded = false;
    try {
      const res = await fetchNewOrders();
      if (res?.success && res?.data) {
        // Тоолуур серверийн үлдэгдлээс энэ мөчөөс хойш тоологдоно (utils/order-utils.js).
        const fetchedAt = Date.now();
        setOrders(res.data.map((order) => ({ ...order, _fetchedAt: fetchedAt })));

        /* ⚠️ Шинэчлэлт 20 сек болсон тул захиалга байх бүрд дуудлага нээвэл мерчант
           хаасны дараа 20 сек-ийн дотор дахин дуугарна. ШИНЭ дугаар ирвэл тэр даруй,
           хүлээж аваагүй хэвээр бол урьдын адил 2 минут тутам сануулна. */
        const newOrders = res.data.filter((order) => order.statusId === 2);
        const unseen = newOrders.some((o) => !alertedIdsRef.current.has(o.id));
        const remindDue = Date.now() - lastAlertAtRef.current >= NEW_ORDER_REMIND_MS;
        if (newOrders.length > 0 && (unseen || remindDue)) {
          newOrders.forEach((o) => alertedIdsRef.current.add(o.id));
          lastAlertAtRef.current = Date.now();
          setNewOrderCount(newOrders.length);
          setNewOrderModalOpen(true);
          // Дуу дахин эхлүүлэх дохио (modal нээлттэй байсан ч шинэ захиалгад дуугарна).
          setNewOrderTick((t) => t + 1);
        }
        succeeded = true;
      } else {
        console.error(res?.error || "Failed to fetch new orders.");
      }
    } catch (error) {
      console.error("pollOrders error:", error);
    } finally {
      if (!silent) setOrdersLoading(false);
    }

    // Дараалсан алдааг тоолно: POLL_FAIL_BANNER_THRESHOLD удаа дараалан
    // бүтэлгүйтвэл улаан банер гарч, дараагийн амжилттай poll-оор алга болно.
    // (Зөвхөн ref + setter ашиглана — setInterval-ийн хуучин closure-оос ч зөв ажиллана)
    if (succeeded) {
      pollFailCountRef.current = 0;
      setOrdersPollFailing(false);
    } else {
      pollFailCountRef.current += 1;
      if (pollFailCountRef.current >= POLL_FAIL_BANNER_THRESHOLD) {
        setOrdersPollFailing(true);
      }
    }
  };

  useEffect(() => {
    fetchMerchantInfo();
  }, []);

  useEffect(() => {
    let intervalId;
    if (merchantInfo.isOpen) {
      pollOrders();
      intervalId = setInterval(() => {
        pollOrders({ silent: true });
      }, POLL_MS);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
      // Polling зогсоход (дэлгүүр хаагдсан / системээс гарсан) тоолуур, банерыг цэвэрлэнэ
      resetPollFailures();
    };
  }, [merchantInfo.isOpen]);

  /* Tab фокус буцахад ШУУД дахин татна (catch-up). Хөтөч фонд setInterval-ийг
     ~1 минутад удаашруулдаг тул өөр tab-аас буцахад захиалга хоцорч харагддаг
     байсныг багасгана. */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && merchantInfo.isOpen) {
        pollOrders({ silent: true });
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantInfo.isOpen]);

  /* SOCKET REALTIME (Фаз 1.3) — шинэ захиалгыг ШУУД хүлээн авна.
     WebSocket мессеж нь setInterval шиг фонд throttle хийгддэггүй тул өөр tab-д
     байх зуур ч захиалга хоцролгүй ирнэ. Backend `order:new`-г `merchant:<id>`
     өрөөнд цацна (lib/socket.js). Хүлээн авмагц pollOrders → дуу/modal.
     Нэвтэрсэн (merchantId бий) үед л холбоно; гарахад салгана. */
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || !merchantInfo.id) return undefined;

    const socket = io(API_URL, {
      auth: { token },
      transports: ["websocket", "polling"],
    });
    const onNewOrder = () => pollOrders({ silent: true });
    socket.on("order:new", onNewOrder);

    return () => {
      socket.off("order:new", onNewOrder);
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantInfo.id]);

  /**
   * Бэлтгэх хугацааг сунгах (10|20 мин, 1 удаа). Амжилттай бол жагсаалтыг шууд
   * шинэчилнэ. Алдааны мессежийг буцаана (null = амжилттай).
   */
  const extendPrep = async (orderId, minutes) => {
    try {
      const res = await extendPrepTime({ orderId, minutes });
      if (!res?.success) return res?.message || "Хугацаа сунгаж чадсангүй.";
      // Сервер prepDuration/prepDeadlineAt-ыг сунгана; дараагийн poll шинэ
      // prepRemainingSec-ийг буцаана — localStorage хөндөхгүй.
      await pollOrders({ silent: true });
      return null;
    } catch (error) {
      return error?.response?.data?.message || "Хугацаа сунгаж чадсангүй.";
    }
  };

  return (
    <MerchantContext.Provider
      value={{
        extendPrep,
        ...merchantInfo,
        isFullScreen,
        toggleFullScreen,
        saveMerchantInfo,
        clearMerchantInfo,
        orders,
        ordersLoading,
        newOrderModalOpen,
        setNewOrderModalOpen,
        newOrderCount,
        newOrderTick,
        initialLoading,
        pollOrders,
        ordersPollFailing,
      }}
    >
      {children}
    </MerchantContext.Provider>
  );
}
